import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { errorHandler } from "../utils/errorHandler";
import { apiDelete, apiGet, apiPost, apiPut } from "src/utils/axios";
import {
  getRFPStatusUrl,
  getBidders,
  getProposalStatusUrl,
  postFileUploadURL,
  PostNewTemplateUrl,
  getRfpById,
  publicFileUpload,
  getRfpPreviewById
} from "src/utils/pathConst";
import {
  BiddersResponseTypes,
  DraftNewRFParams,
  FileUploadParams,
  NewRFPTemplateParams,
  RfpByIdModel,
  RfpByIdParams,
  RfpStatusListModel,
  RfpStatusResponseTypes,
  UpdateDraftRFParams,
} from "src/models/rfp";

export type ErrorInformation = {
  message: string;
  status: number;
};

type RfpState = {
  loading: boolean;
  error: ErrorInformation;
  rfp: any;
  rfpStatus: RfpStatusResponseTypes;
  rfpListStatus: RfpStatusListModel;
  biddersList: object;
  rfpListById: object;
  rfpDataById: any;
  rfpPreviewDataById: any;
  rfpDataByTemplate: any;
};

const initialState: RfpState = {
  loading: true,
  error: null,
  rfp: null,
  rfpStatus: null,
  rfpListStatus: null,
  biddersList: null,
  rfpListById: null,
  rfpDataById: null,
  rfpPreviewDataById: null,
  rfpDataByTemplate: null,
};

export const getRfpStatusEnumData = createAsyncThunk<
  RfpStatusResponseTypes,
  {},
  {
    rejectValue: ErrorInformation;
  }
>("select/getRfpStatusEnumData", async ({}, { rejectWithValue }) => {
  try {
    const getResponse = await apiGet(`${getRFPStatusUrl}`);
    return getResponse.data;
  } catch (error) {
    return rejectWithValue(errorHandler(error));
  }
});

export const getBiddersList = createAsyncThunk<
  BiddersResponseTypes,
  { queryParam },
  {
    rejectValue: ErrorInformation;
  }
>("select/getBiddersList", async ({ queryParam }, { rejectWithValue }) => {
  // const jurisdiction = new URLSearchParams(queryParam.jurisdiction).toString();
  // const pricingModel = new URLSearchParams(queryParam.pricing_model).toString();
  try {
    const getResponse = await apiGet(
      `${getBidders}?jurisdiction=[${queryParam.jurisdiction}]&pricing_model=[${queryParam.pricing_model}]&searchString=${queryParam.search_string}&offset=${queryParam.offset}&limit=${queryParam.limit}`
    );
    return getResponse.data;
  } catch (error) {
    return rejectWithValue(errorHandler(error));
  }
});
export const postRfpFileUpload = createAsyncThunk<
  FileUploadParams,
  { bodyParams: any },
  {
    rejectValue: ErrorInformation;
  }
>("select/PostFileUpload", async ({ bodyParams }, { rejectWithValue }) => {
  try {
    const response = await apiPost(postFileUploadURL, bodyParams);
    return response.data;
  } catch (error) {
    return rejectWithValue(errorHandler(error));
  }
});

export const publicfileUpload = createAsyncThunk<
  FileUploadParams,
  { bodyParams: any },
  {
    rejectValue: ErrorInformation;
  }
>("select/PublicFileUpload", async ({ bodyParams }, { rejectWithValue }) => {
  try {
    const response = await apiPost(publicFileUpload, bodyParams);
    return response.data;
  } catch (error) {
    return rejectWithValue(errorHandler(error));
  }
}); 
export const postNewRFP = createAsyncThunk<
  DraftNewRFParams,
  { bodyParams: any },
  {
    rejectValue: ErrorInformation;
  }
>("select/PostNewRFP", async ({ bodyParams }, { rejectWithValue }) => {
  try {
    const response = await apiPost(getProposalStatusUrl, bodyParams);
    return response.data;
  } catch (error) {
    return rejectWithValue(errorHandler(error));
  }
});

export const updateRfpById = createAsyncThunk<
  DraftNewRFParams,
  { bodyParams: any },
  {
    rejectValue: ErrorInformation;
  }
>("select/UpdateRfpById", async ({ bodyParams }, { rejectWithValue }) => {
  try {
    const response = await apiPut(
      `${getProposalStatusUrl}/${bodyParams.id}`,
      bodyParams.postData
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(errorHandler(error));
  }
});

export const postNewDraftRFP = createAsyncThunk<
  DraftNewRFParams,
  { bodyParams: any },
  {
    rejectValue: ErrorInformation;
  }
>("select/PostNewRFP", async ({ bodyParams }, { rejectWithValue }) => {
  try {
    const response = await apiPost(getProposalStatusUrl + "/draft", bodyParams);
    return response.data;
  } catch (error) {
    return rejectWithValue(errorHandler(error));
  }
});

export const updateNewDraftRFP = createAsyncThunk<
  UpdateDraftRFParams,
  { bodyParams: any },
  {
    rejectValue: ErrorInformation;
  }
>("select/updateNewDraftRFP", async ({ bodyParams }, { rejectWithValue }) => {
  try {
    const response = await apiPost(getProposalStatusUrl + "/draft", bodyParams);
    return response.data;
  } catch (error) {
    return rejectWithValue(errorHandler(error));
  }
});
export const postNewRfpTemplate = createAsyncThunk<
  NewRFPTemplateParams,
  { bodyParams: any },
  {
    rejectValue: ErrorInformation;
  }
>("select/PostNewRFPTemplate", async ({ bodyParams }, { rejectWithValue }) => {
  try {
    const response = await apiPost(PostNewTemplateUrl, bodyParams);
    return response.data;
  } catch (error) {
    return rejectWithValue(errorHandler(error));
  }
});

// get RFP by Id
export const getRFPDetailsById = createAsyncThunk<
  RfpByIdModel,
  {},
  {
    rejectValue: ErrorInformation;
  }
>("select/RfpByIdList", async (params: RfpByIdParams, { rejectWithValue }) => {
  try {
    const getResponse = await apiGet(`${getRfpById}/${params?.rfp_id}`);
    return getResponse.data.detail;
  } catch (error) {
    return rejectWithValue(errorHandler(error));
  }
});

// get RFP Preview Data
export const getRFPPreviewDetailsById = createAsyncThunk<
  RfpByIdModel,
  {},
  {
    rejectValue: ErrorInformation;
  }
>("select/getRFPPreviewDetailsById", async (params: RfpByIdParams, { rejectWithValue }) => {
  try {
    const getResponse = await apiGet(`${getRfpPreviewById}/${params?.rfp_id}`);
    return getResponse.data.detail;
  } catch (error) {
    return rejectWithValue(errorHandler(error));
  }
});

export const deleteUploadedFile = createAsyncThunk<
  any,
  { bodyParams: any },
  {
    rejectValue: ErrorInformation;
  }
>(
  "TcsConnect/deleteTcsCorpUserGroup",
  async ({ bodyParams }, { rejectWithValue }) => {
    try {
      const response = await apiDelete(
        `${getRfpById}/${bodyParams.userGroupId}`,
        bodyParams
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(errorHandler(error));
    }
  }
);

export const rfpSlice = createSlice({
  name: "rfp",
  initialState,
  reducers: {
    handleResetRfpDetail: (state) => {
      state.rfpDataById = null;
    },
    setRfpDataByTemplate: (state, action) => {
      state.rfpDataByTemplate = action?.payload;
    },
    resetRfpDataByTemplate: (state) => {
      state.rfpDataByTemplate = null;
    },
   
  },
  extraReducers: {
    //post rfp information

    //post rfp status information
    [getRfpStatusEnumData.pending.type]: (state) => {
      state.loading = true;
      console.log(state);
    },
    [getRfpStatusEnumData.fulfilled.type]: (
      state,
      action: PayloadAction<RfpStatusResponseTypes | null>
    ) => {
      console.log(action);
      state.rfpStatus = action.payload;
      state.loading = false;
    },
    [getRfpStatusEnumData.rejected.type]: (
      state,
      action: PayloadAction<ErrorInformation | null>
    ) => {
      state.loading = false;
      state.error = action.payload;
    },

    //get bidders information
    [getBiddersList.pending.type]: (state) => {
      state.loading = true;
    },
    [getBiddersList.fulfilled.type]: (
      state,
      action: PayloadAction<RfpStatusResponseTypes | null>
    ) => {
      state.biddersList = action.payload;
      state.loading = false;
    },
    [getBiddersList.rejected.type]: (
      state,
      action: PayloadAction<ErrorInformation | null>
    ) => {
      state.loading = false;
      state.error = action.payload;
    },
    [getRFPPreviewDetailsById.pending.type]: (state) => {
      state.loading = true;
    },
    [getRFPPreviewDetailsById.fulfilled.type]: (
      state,
      action: PayloadAction<any | null>
    ) => {
      state.rfpPreviewDataById = action.payload;
      state.loading = false;
    },
    [getRFPPreviewDetailsById.rejected.type]: (
      state,
      action: PayloadAction<ErrorInformation | null>
    ) => {
      state.loading = false;
      state.error = action.payload;
    },
    [getRFPDetailsById.pending.type]: (state) => {
      state.loading = true;
    },
    [getRFPDetailsById.fulfilled.type]: (
      state,
      action: PayloadAction<any | null>
    ) => {
      state.rfpDataById = action.payload;
      state.loading = false;
    },
    [getRFPDetailsById.rejected.type]: (
      state,
      action: PayloadAction<ErrorInformation | null>
    ) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
});
export const {
  handleResetRfpDetail,
  setRfpDataByTemplate,
  resetRfpDataByTemplate,
} = rfpSlice.actions;
